import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Layout from "@/components/app/Layout";
import { Trash2, ShieldCheck, Shield, Eye, Pencil, X, Save, FolderOpen, UserPlus, Copy, Mail, RefreshCw } from "lucide-react";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/context/AuthContext";
import AdminSubnav from "@/components/app/AdminSubnav";
import AdminUserFilesModal from "@/components/app/AdminUserFilesModal";

export default function Admin() {
  const { user: currentUser, startImpersonate } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "" });
  const [saving, setSaving] = useState(false);
  const [filesUser, setFilesUser] = useState(null);

  // Invite user modal
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", name: "", role: "user" });
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null); // { invite_link, email_sent, user }
  const [regenLoading, setRegenLoading] = useState(null);

  const submitInvite = async (e) => {
    e?.preventDefault();
    setInviting(true);
    try {
      const { data } = await api.post("/admin/users/invite", inviteForm);
      setInviteResult(data);
      toast.success(`Compte créé pour ${inviteForm.name}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec");
    } finally {
      setInviting(false);
    }
  };

  const closeInvite = () => {
    setInviteOpen(false);
    setInviteResult(null);
    setInviteForm({ email: "", name: "", role: "user" });
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link).then(
      () => toast.success("Lien copié"),
      () => toast.error("Copie impossible — sélectionnez le lien manuellement")
    );
  };

  const regenerateInvite = async (u) => {
    setRegenLoading(u.user_id);
    try {
      const { data } = await api.post(`/admin/users/${u.user_id}/regenerate-invite`);
      setInviteResult({ invite_link: data.invite_link, email_sent: false, user: u });
      setInviteOpen(true);
      toast.success("Nouveau lien d'invitation généré");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec");
    } finally {
      setRegenLoading(null);
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch {
      toast.error("Accès refusé ou erreur");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleRole = async (u) => {
    // Cycle: user → vp → admin → user
    const next = u.role === "admin" ? "user" : (u.role === "vp" ? "admin" : "vp");
    try {
      await api.put(`/admin/users/${u.user_id}/role`, { role: next });
      toast.success(`Rôle mis à jour: ${next}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    }
  };

  const setRole = async (u, role) => {
    try {
      await api.put(`/admin/users/${u.user_id}/role`, { role });
      toast.success(`Rôle mis à jour: ${role}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Supprimer ${u.email} ?\nToutes ses factures seront supprimées.`)) return;
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      toast.success("Utilisateur supprimé");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    }
  };

  const impersonate = (u) => {
    if (u.user_id === currentUser?.user_id) {
      toast.info("Vous êtes déjà connecté sur ce compte");
      return;
    }
    startImpersonate(u.user_id);
  };

  const openEdit = (u) => {
    setEditing(u);
    setEditForm({ name: u.name || "", email: u.email || "" });
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/users/${editing.user_id}/profile`, editForm);
      toast.success("Profil mis à jour");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <Toaster richColors position="top-right" />
      <AdminSubnav />
      <p className="overline mb-2">Administration</p>
      <h1 className="font-heading text-3xl sm:text-5xl font-black tracking-tighter leading-tight sm:leading-none mb-3">
        Utilisateurs
      </h1>
      <p className="text-gray-500 mb-6 max-w-lg">
        Contrôle total : rôles, profils (nom, courriel), et accès au compte de chaque utilisateur via « Voir en tant que ».
      </p>

      <div className="mb-6">
        <button
          className="btn-primary"
          onClick={() => setInviteOpen(true)}
          data-testid="invite-user-button"
        >
          <UserPlus size={14} /> Inviter un utilisateur
        </button>
        <p className="text-xs text-gray-400 mt-1">Crée un compte et envoie un lien d'activation où l'utilisateur choisira son mot de passe.</p>
      </div>

      <div className="ra-card" data-testid="admin-users-card">
        {loading && <div className="progress-line" />}
        {/* Mobile card list */}
        <div className="md:hidden divide-y divide-gray-100" data-testid="admin-users-mobile">
          {users.map((u) => (
            <div key={u.user_id} className="p-4" data-testid={`admin-row-${u.user_id}`}>
              <div className="flex items-start gap-3 mb-3">
                {u.picture
                  ? <img src={u.picture} alt="" className="w-10 h-10 rounded-full border border-gray-200 shrink-0" />
                  : <div className="w-10 h-10 rounded-full bg-gray-200 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      className="font-semibold truncate text-left hover:underline"
                      onClick={() => setFilesUser(u)}
                      data-testid={`view-files-name-${u.user_id}`}
                    >
                      {u.name}
                    </button>
                    <span className={`badge ${u.role === "admin" ? "admin" : (u.role === "vp" ? "!bg-violet-100 !text-violet-800" : "")}`}>
                      {u.role === "admin" ? "Admin" : u.role === "vp" ? "VP" : "Utilisateur"}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 truncate">{u.email}</div>
                  <div className="text-xs text-gray-400 mt-0.5">
                    <span className="num">{u.invoice_count ?? 0}</span> facture(s)
                    {u.created_at && <> · {String(u.created_at).slice(0,10)}</>}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-5 gap-2">
                <button
                  className="btn-secondary text-xs justify-center py-2"
                  onClick={() => setFilesUser(u)}
                  title="Voir les fichiers / imprimer"
                  data-testid={`view-files-${u.user_id}`}
                >
                  <FolderOpen size={14} />
                </button>
                <button
                  className="btn-secondary text-xs justify-center py-2"
                  onClick={() => impersonate(u)}
                  disabled={u.user_id === currentUser?.user_id}
                  data-testid={`impersonate-${u.user_id}`}
                >
                  <Eye size={14} />
                </button>
                <button
                  className="btn-secondary text-xs justify-center py-2"
                  onClick={() => openEdit(u)}
                  data-testid={`edit-profile-${u.user_id}`}
                >
                  <Pencil size={14} />
                </button>
                <button
                  className="btn-secondary text-xs justify-center py-2"
                  onClick={() => toggleRole(u)}
                  disabled={u.user_id === currentUser?.user_id && u.role === "admin"}
                  data-testid={`toggle-role-${u.user_id}`}
                >
                  {u.role === "admin" ? <ShieldCheck size={14} /> : <Shield size={14} />}
                </button>
                <button
                  className="btn-danger text-xs justify-center py-2"
                  onClick={() => remove(u)}
                  disabled={u.user_id === currentUser?.user_id}
                  data-testid={`delete-user-${u.user_id}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
          {!loading && users.length === 0 && (
            <div className="text-center text-gray-400 py-12">Aucun utilisateur</div>
          )}
        </div>
        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="ra-table">
            <thead>
              <tr>
                <th style={{width: 40}}></th>
                <th>Nom</th>
                <th>Courriel</th>
                <th>Rôle</th>
                <th className="num">Factures</th>
                <th>Créé le</th>
                <th style={{width: 270}}></th>
              </tr>
            </thead>
            <tbody data-testid="admin-users-tbody">
              {users.map((u) => (
                <tr key={u.user_id} data-testid={`admin-row-desktop-${u.user_id}`}>
                  <td>
                    {u.picture
                      ? <img src={u.picture} alt="" className="w-7 h-7 rounded-full border border-gray-200" />
                      : <div className="w-7 h-7 rounded-full bg-gray-200" />}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="font-medium text-left hover:underline"
                      onClick={() => setFilesUser(u)}
                      data-testid={`view-files-name-desktop-${u.user_id}`}
                    >
                      {u.name}
                    </button>
                    {u.user_id === currentUser?.user_id && <span className="text-xs text-gray-400 ml-1">(vous)</span>}
                  </td>
                  <td className="text-sm">{u.email}</td>
                  <td>
                    <select
                      className="ra-input !py-1 !px-2 text-xs"
                      value={u.role || "user"}
                      onChange={(e) => setRole(u, e.target.value)}
                      disabled={u.user_id === currentUser?.user_id && u.role === "admin"}
                      data-testid={`role-select-desktop-${u.user_id}`}
                    >
                      <option value="user">Utilisateur</option>
                      <option value="vp">Vice-président</option>
                      <option value="admin">Administrateur</option>
                    </select>
                  </td>
                  <td className="num">{u.invoice_count ?? 0}</td>
                  <td className="text-xs text-gray-500">{u.created_at ? String(u.created_at).slice(0,10) : "—"}</td>
                  <td>
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        className="btn-ghost"
                        onClick={() => setFilesUser(u)}
                        title="Voir les fichiers PDF/Excel"
                        data-testid={`view-files-desktop-${u.user_id}`}
                      >
                        <FolderOpen size={14} />
                      </button>
                      <button
                        className="btn-ghost"
                        onClick={() => impersonate(u)}
                        disabled={u.user_id === currentUser?.user_id}
                        title="Voir en tant que cet utilisateur"
                        data-testid={`impersonate-desktop-${u.user_id}`}
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        className="btn-ghost"
                        onClick={() => openEdit(u)}
                        title="Éditer le profil"
                        data-testid={`edit-profile-desktop-${u.user_id}`}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        className="btn-ghost"
                        onClick={() => toggleRole(u)}
                        disabled={u.user_id === currentUser?.user_id && u.role === "admin"}
                        title="Basculer rôle"
                        data-testid={`toggle-role-desktop-${u.user_id}`}
                      >
                        {u.role === "admin" ? <ShieldCheck size={14} /> : <Shield size={14} />}
                      </button>
                      <button
                        className="btn-ghost text-red-600 hover:bg-red-50"
                        onClick={() => remove(u)}
                        disabled={u.user_id === currentUser?.user_id}
                        data-testid={`delete-user-desktop-${u.user_id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && users.length === 0 && (
                <tr><td colSpan={7} className="text-center text-gray-400 py-12">Aucun utilisateur</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="edit-profile-modal">          <div className="bg-white border-2 border-[#0B0F19] w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <div>
                <p className="overline">Modifier le profil</p>
                <div className="font-heading font-black text-2xl tracking-tighter">{editing.name}</div>
              </div>
              <button onClick={() => setEditing(null)} className="btn-ghost"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="ra-label">Nom</label>
                <input
                  className="ra-input"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  data-testid="edit-name-input"
                />
              </div>
              <div>
                <label className="ra-label">Courriel</label>
                <input
                  className="ra-input"
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  data-testid="edit-email-input"
                />
                <p className="text-xs text-gray-500 mt-1">Doit correspondre au courriel Google lors de la prochaine connexion.</p>
              </div>
            </div>
            <div className="p-5 border-t border-gray-200 flex gap-2 justify-end">
              <button className="btn-secondary" onClick={() => setEditing(null)}>Annuler</button>
              <button className="btn-primary" onClick={saveEdit} disabled={saving} data-testid="save-profile-button">
                <Save size={14} /> {saving ? "Enregistrement..." : "Enregistrer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {filesUser && (
        <AdminUserFilesModal user={filesUser} onClose={() => setFilesUser(null)} />
      )}

      {inviteOpen && (
        <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="invite-modal">
          <div className="bg-white dark:bg-neutral-900 border-2 border-[#0B0F19] dark:border-white w-full max-w-md">
            <div className="p-5 border-b border-gray-200 dark:border-neutral-700 flex items-center justify-between">
              <div>
                <p className="overline">Inviter un utilisateur</p>
                <div className="font-heading font-black text-xl tracking-tight">
                  {inviteResult ? "Lien d'activation prêt" : "Nouveau compte"}
                </div>
              </div>
              <button onClick={closeInvite} className="btn-ghost"><X size={16} /></button>
            </div>

            {!inviteResult ? (
              <form onSubmit={submitInvite}>
                <div className="p-5 space-y-4">
                  <div>
                    <label className="ra-label">Nom complet <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      className="ra-input"
                      value={inviteForm.name}
                      onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                      required
                      data-testid="invite-name-input"
                      placeholder="Jean Tremblay"
                    />
                  </div>
                  <div>
                    <label className="ra-label">Courriel <span className="text-red-500">*</span></label>
                    <input
                      type="email"
                      className="ra-input"
                      value={inviteForm.email}
                      onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                      required
                      data-testid="invite-email-input"
                      placeholder="jean@labriva.com"
                    />
                  </div>
                  <div>
                    <label className="ra-label">Rôle <span className="text-red-500">*</span></label>
                    <select
                      className="ra-input"
                      value={inviteForm.role}
                      onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                      data-testid="invite-role-input"
                    >
                      <option value="user">Utilisateur (employé)</option>
                      <option value="vp">Vice-président</option>
                      <option value="admin">Administrateur</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">L'utilisateur recevra un lien pour activer son compte et choisir son mot de passe.</p>
                  </div>
                </div>
                <div className="p-5 border-t border-gray-200 dark:border-neutral-700 flex gap-2 justify-end">
                  <button type="button" className="btn-secondary" onClick={closeInvite}>Annuler</button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={inviting || !inviteForm.email || !inviteForm.name}
                    data-testid="invite-submit-button"
                  >
                    <Mail size={14} /> {inviting ? "Création..." : "Créer et générer le lien"}
                  </button>
                </div>
              </form>
            ) : (
              <div>
                <div className="p-5 space-y-4">
                  <div className="bg-green-50 dark:bg-green-950/30 border-l-4 border-green-500 p-3 text-sm">
                    <strong className="text-green-800 dark:text-green-300">Compte créé pour {inviteResult.user?.name || inviteForm.name}</strong>
                    <p className="text-green-700 dark:text-green-400 text-xs mt-1">
                      {inviteResult.email_sent
                        ? "Un courriel d'invitation a été envoyé."
                        : "Le courriel automatique n'a pas pu être envoyé. Copiez et envoyez le lien ci-dessous manuellement."}
                    </p>
                  </div>
                  <div>
                    <label className="ra-label">Lien d'activation (valide 7 jours)</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        readOnly
                        className="ra-input flex-1 text-xs"
                        value={inviteResult.invite_link}
                        onClick={(e) => e.target.select()}
                        data-testid="invite-link-display"
                      />
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => copyLink(inviteResult.invite_link)}
                        data-testid="copy-invite-link"
                      >
                        <Copy size={14} /> Copier
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">Envoyez ce lien à l'utilisateur (par SMS, courriel personnel, Slack, etc.) — il devra cliquer dessus puis choisir son mot de passe.</p>
                  </div>
                </div>
                <div className="p-5 border-t border-gray-200 dark:border-neutral-700 flex gap-2 justify-end">
                  <button className="btn-primary" onClick={closeInvite}>Fermer</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}
